import { Controller } from "@hotwired/stimulus"

// Handles modal dialogs
export default class extends Controller {
  static targets = ["panel", "backdrop"]
  static values = { id: String }
  
  // Keep track of hidden buttons to restore them properly
  static hiddenButtonsMap = new Map()

  connect() {
    // Prevent page scrolling when modal is open
    this.originalOverflow = document.body.style.overflow
    console.log("Modal controller connected", this.element.id)
  }

  disconnect() {
    // Restore original overflow
    document.body.style.overflow = this.originalOverflow
    console.log("Modal controller disconnected", this.element.id)
    
    // Ensure buttons are restored when the modal controller is disconnected
    if (this.element.id) {
      this.restoreButtonsForModal(this.element.id)
    }
  }

  open(event) {
    console.log("Modal open called", event?.currentTarget?.textContent?.trim())
    
    // Check if the trigger has a modal-id-value attribute
    if (event && event.currentTarget.hasAttribute("data-modal-id-value")) {
      const modalId = event.currentTarget.getAttribute("data-modal-id-value");
      console.log("Opening modal with ID:", modalId)
      const modal = document.getElementById(modalId);
      
      if (modal) {
        // Store the current trigger button to restore it later
        try {
          this.storeButton(event.currentTarget, modalId)
          console.log("Stored trigger:", event.currentTarget.textContent?.trim())
        } catch (e) {
          console.error("Error storing trigger:", e)
        }
        
        // Hide all buttons with the modal controller except the current modal's close button
        try {
          const buttons = document.querySelectorAll('button[data-controller="modal"], a[data-controller="modal"]');
          console.log("Found buttons to hide:", buttons.length)
          
          buttons.forEach(button => {
            // Skip buttons with modal#close action that are inside the current modal
            if (button.getAttribute('data-action') === 'modal#close' && 
                button.closest(`#${modalId}`)) {
              return;
            }
            
            if (!button.classList.contains('hidden')) {
              button.classList.add('hidden');
              this.storeButton(button, modalId)
              console.log("Hidden button:", button.textContent?.trim())
            }
          });
        } catch (e) {
          console.error("Error hiding buttons:", e)
        }
        
        // Open the target modal
        modal.classList.remove("hidden");
        const backdrop = modal.querySelector('[data-modal-target="backdrop"]');
        if (backdrop) {
          backdrop.classList.remove("hidden");
        }
        document.body.style.overflow = "hidden";
        console.log("Opened modal:", modalId)
        
        // Focus the first focusable element
        setTimeout(() => {
          const panel = modal.querySelector('[data-modal-target="panel"]');
          if (panel) {
            const focusable = panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (focusable) {
              focusable.focus();
            }
          }
        }, 50);
        
        return;
      }
    }
    
    // Default behavior if no target modal is specified
    this.element.classList.remove("hidden");
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("hidden");
    }
    document.body.style.overflow = "hidden";
    
    // Focus the first focusable element if we have a panel target
    setTimeout(() => {
      if (this.hasPanelTarget) {
        const focusable = this.panelTarget.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable) {
          focusable.focus();
        }
      }
    }, 50);
  }

  close() {
    console.log("Modal close called")
    
    // Hide the modal
    this.element.classList.add("hidden");
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden");
    }
    document.body.style.overflow = this.originalOverflow;
    console.log("Closed modal:", this.element.id)
    
    // Only restore buttons associated with this modal
    if (this.element.id) {
      this.restoreButtonsForModal(this.element.id)
    }
  }
  
  // Store a button that was hidden, associated with a specific modal
  storeButton(button, modalId) {
    if (!this.constructor.hiddenButtonsMap.has(modalId)) {
      this.constructor.hiddenButtonsMap.set(modalId, new Set())
    }
    
    // Store a reference to the button element
    this.constructor.hiddenButtonsMap.get(modalId).add(button)
  }
  
  // Restore all buttons associated with a specific modal
  restoreButtonsForModal(modalId) {
    try {
      if (this.constructor.hiddenButtonsMap.has(modalId)) {
        const buttons = this.constructor.hiddenButtonsMap.get(modalId)
        console.log(`Restoring ${buttons.size} buttons for modal ${modalId}`)
        
        buttons.forEach(button => {
          if (button && button.classList && button.classList.contains('hidden')) {
            button.classList.remove('hidden')
            console.log("Restored button:", button.textContent?.trim())
          }
        })
        
        // Clear this modal's button map
        this.constructor.hiddenButtonsMap.delete(modalId)
      }
    } catch (e) {
      console.error("Error restoring buttons:", e)
    }
  }
}